import React, { useState } from "react";
import { Button } from "@/components/shared/ui/button";
import { Loader2 } from "lucide-react";
import { startLL97Filing, startLL88Filing, syncLl87Submission } from "@/api/audit";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "@/queries/useToast";
import { BuildingType } from "@/interfaces/Building";

interface StartFilingButtonProps {
  buildingId: string;
  building: BuildingType;
  filingType?: "LL97" | "LL88" | "LL87" | "BERDO";
  submission?: any;
  className?: string;
}

export const StartFilingButton = ({
  buildingId,
  building,
  filingType = "LL97",
  submission,
  className,
}: StartFilingButtonProps) => {
  const [isStarting, setIsStarting] = useState(false);
  const queryClient = useQueryClient();
  const buildingData = building;

  // Read the submission ID either from props or from building data
  const ll97SubmissionId =
    submission?.id || buildingData?.ll97Submissions?.[0]?.id;
  const ll88SubmissionId =
    submission?.id || buildingData?.ll88Submissions?.[0]?.id;

  const navigate = useNavigate();

  const handleStartLL97Click = () => {
    setIsStarting(true);
    startLL97Filing(buildingId)
      .then((response) => {
        queryClient.invalidateQueries();
        setIsStarting(false);
        // Get the submission ID from the response or use the existing one
        const submissionId = response?.data?.id || ll97SubmissionId;
        if (submissionId) {
          navigate({
            to: "/buildings/$buildingId/ll97/$ll97SubmissionId",
            params: { buildingId, ll97SubmissionId: submissionId },
          });
        }
      })
      .catch((error) => {
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || "Failed to start LL97 filing",
        });
        setIsStarting(false);
      });
  };

  const handleStartLL88Click = () => {
    setIsStarting(true);
    startLL88Filing(buildingId)
      .then((response) => {
        queryClient.invalidateQueries();
        setIsStarting(false);
        // Get the submission ID from the response or use the existing one
        const submissionId = response?.data?.id || ll88SubmissionId;
        if (submissionId) {
          navigate({
            to: "/buildings/$buildingId/ll88/$ll88SubmissionId",
            params: { buildingId, ll88SubmissionId: submissionId },
          });
        }
      })
      .catch((error) => {
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || "Failed to start LL88 filing",
        });
        setIsStarting(false);
      });
  };

  const handleStartLL87Click = () => {
    setIsStarting(true);
    syncLl87Submission(buildingId)
      .then((data) => {
        queryClient.invalidateQueries();
        setIsStarting(false);
        const submissionId = data?.id;
        if (submissionId) {
          navigate({
            to: "/portal/buildings/$buildingId/ll87/$ll87SubmissionId",
            params: { buildingId, ll87SubmissionId: submissionId },
          });
        }
      })
      .catch((error) => {
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || "Failed to sync LL87 submission",
        });
        setIsStarting(false);
      });
  };

  const handleStartBERDOClick = () => {
    console.log("handleStartBERDOClick", buildingId, building, submission);
  };

  const handleStartClick = () => {
    if (filingType === "LL88") {
      handleStartLL88Click();
    } else if (filingType === "LL97") {
      handleStartLL97Click();
    } else if (filingType === "LL87") {
      handleStartLL87Click();
    } else if (filingType === "BERDO") {
      handleStartBERDOClick();
    }
  };

  return (
    <div className="flex items-center">
      <Button
        variant="default"
        onClick={handleStartClick}
        className={className}
      >
        {isStarting ? (
          <div className="flex items-center">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Starting...
          </div>
        ) : (
          "Begin filing"
        )}
      </Button>
    </div>
  );
};
